/* ============================================================
   Game View — page component.
   Mount at e.g.  <Route path="/game/:fixtureId" element={<GameView />} />
   Chrome (navbar) comes from App.js; this renders page content only,
   inside a `.rh-gameview` scope so its CSS can't leak.
   ============================================================ */
import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import './GameView.css';
import { useGameView } from './useGameView';
import { GvTreeLayout, GvMasterDetail, GvSplitLayout } from './gameViewLayouts';
import { gvK, gvMoney, gvPct, gvLiClass, gvRollPlayer, GvSharpCell, GvGateBadge } from './gameViewUtils';

/* ---------- game header ---------- */
function GameHead({ game, live }) {
  const { home, away } = game;
  return (
    <div className="card gv-gamehead">
      <div className="gv-gamehead-left">
        <div className="gv-teams">
          <div className="gv-team">
            <div className="gv-crest" style={{ background: home.color }}>{home.abbr}</div>
            <div>
              <div className="gv-team-name">{home.name}</div>
              <div className="gv-team-rec">{home.record}</div>
            </div>
          </div>
          {live
            ? <div className="gv-score">{game.live.score.home}<span className="dash">–</span>{game.live.score.away}</div>
            : <div className="gv-vs">vs</div>}
          <div className="gv-team gv-team--away">
            <div className="gv-crest" style={{ background: away.color }}>{away.abbr}</div>
            <div>
              <div className="gv-team-name">{away.name}</div>
              <div className="gv-team-rec">{away.record}</div>
            </div>
          </div>
        </div>
      </div>
      <div className="gv-gamemeta">
        <div className="gv-league-row">
          <span className="gv-league">{game.league}</span>
          {live
            ? <span className="gv-status gv-status--live"><span className="pulse" />Live</span>
            : <span className="gv-status gv-status--pre">Pre-game</span>}
        </div>
        {live
          ? <div className="gv-clock"><span className="pulse" />{game.live.minute} · {game.live.period}</div>
          : <div className="gv-kickoff">Kickoff {game.kickoff}</div>}
      </div>
    </div>
  );
}

/* ---------- roll-up stat strip ---------- */
function RollupStrip({ stats }) {
  const overShare = stats.feeOver / (stats.feeOver + stats.feeUnder || 1);
  return (
    <div className="gv-rollup">
      <div className="gv-stat" style={{ '--accent': 'var(--sleeper-accent)' }}>
        <span className="gv-stat-label">Game handle</span>
        <span className="gv-stat-value">{gvK(stats.handle)}</span>
        <span className="gv-stat-sub">
          O {stats.nEntriesOver.toLocaleString()} / U {stats.nEntriesUnder.toLocaleString()} entries · {stats.nCombos} combos
        </span>
        <span className="gv-stat-sub">Avg slip O {gvK(stats.avgSlipOver)} / U {gvK(stats.avgSlipUnder)}</span>
      </div>
      <div className="gv-stat" style={{ '--accent': stats.worst > 0 ? 'var(--li-pos)' : 'var(--li-neg)' }}>
        <span className="gv-stat-label">Worst-case net liability</span>
        <span className={`gv-stat-value ${stats.worst > 0 ? 'pos' : 'neg'}`}>{gvMoney(stats.worst, { sign: true })}</span>
        <span className="gv-stat-sub">if every dominant leg lands</span>
      </div>
      <div className="gv-stat" style={{ '--accent': 'var(--li-pos)' }}>
        <span className="gv-stat-label">Concentration</span>
        <span className="gv-stat-value" style={{ fontSize: '1.05rem' }}>{stats.topPlayer.name}</span>
        <span className="gv-stat-sub">{gvMoney(stats.topPlayer.dom, { sign: true })} · biggest single name</span>
      </div>
      <div className="gv-stat" style={{ '--accent': 'var(--li-pos)' }}>
        <span className="gv-stat-label">Over / Under skew</span>
        <span className="gv-stat-value" style={{ fontSize: '1.05rem' }}>{(overShare * 100).toFixed(0)}% over</span>
        <div className="gv-skew">
          <div className="gv-skew-over" style={{ width: `${overShare * 100}%` }} />
          <div className="gv-skew-under" style={{ width: `${(1 - overShare) * 100}%` }} />
        </div>
      </div>
      <div className="gv-stat" style={{ '--accent': 'var(--combo)' }}>
        <span className="gv-stat-label">Combo exposure</span>
        <span className="gv-stat-value" style={{ color: 'var(--combo)' }}>{gvK(stats.comboLiab)}</span>
        <span className="gv-stat-sub">{stats.comboLegs} player legs in combos</span>
      </div>
    </div>
  );
}

/* ---------- mainline (Kalshi) panel ---------- */
function MainlinePanel({ mainline, live }) {
  return (
    <div className="gv-mainline">
      <div className="gv-mainline-head">
        <div className="gv-mainline-title">
          Mainline odds
          <span className="gv-mainline-src">● {mainline.source} · last tick {mainline.lastTick}</span>
        </div>
        <div className="gv-mainline-note">Read-only — team-level exposure is not booked here. Only <span style={{ color: 'var(--combo)' }}>combo-driven</span> liability attaches.</div>
      </div>
      <div className="gv-mainline-grid">
        {mainline.groups.map((g) => (
          <div className="gv-ml-block" key={g.id}>
            <div className="gv-ml-block-title">{g.title}</div>
            {g.rows.map((r, i) => (
              <div className="gv-ml-row" key={i}>
                <span className="gv-ml-sel">{r.sel}{r.line ? <span className="gv-ml-line"> {r.line}</span> : null}</span>
                <span className="gv-flex" style={{ gap: '0.7rem' }}>
                  {r.comboLiab
                    ? <span className="gv-ml-combo" title="combo-driven liability"><span className="dot" />{gvMoney(r.comboLiab, { sign: true })}</span>
                    : <span className="gv-ml-combo gv-ml-combo--none">—</span>}
                  <span className="gv-ml-price">{live ? r.priceLive : r.pricePre}</span>
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- combos tab ---------- */
function CombosTab({ combos }) {
  const comboMax = Math.max(...combos.map((c) => Math.abs(c.liability)));
  return (
    <div>
      <div className="gv-combos-intro">
        <span className="gv-badge gv-badge--combo">New</span>
        <span>
          Combos cross a player pick with a mainline team selection — parlays that couldn't be formed before.
          Straight player exposure lives in the Markets tab; this is the liability that exists <em>only because</em> these legs were combined.
        </span>
      </div>
      <div className="gv-table-wrap">
        <table className="gv-table">
          <thead>
            <tr>
              <th>Combo</th>
              <th className="num">P(fair)</th>
              <th className="num">Handle</th>
              <th className="num" title="Liability carried on the combined line (separate from straight player exposure)">Combo liability</th>
              <th className="num">Sharp</th>
              <th className="num">Entries</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {[...combos].sort((a, b) => Math.abs(b.liability) - Math.abs(a.liability)).map((c) => (
              <tr className="gv-row-line" key={c.id}>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <div className="gv-combo-legs">
                    {c.legs.map((leg, i) => (
                      <div className="gv-combo-leg" key={i}>
                        <span className={`gv-combo-leg-type gv-combo-leg-type--${leg.type}`}>{leg.type === 'player' ? 'Player' : 'Mainline'}</span>
                        <span className="gv-combo-leg-sel">{leg.sel}</span>
                      </div>
                    ))}
                  </div>
                </td>
                <td className="num gv-num">{gvPct(c.p_fair)}</td>
                <td className="num gv-num">{gvK(c.fee)}</td>
                <td className="num"><span className={`gv-num ${gvLiClass(c.liability, comboMax)}`} style={{ color: c.liability > 0 ? 'var(--combo)' : 'var(--li-neg)' }}>{gvMoney(c.liability, { sign: true })}</span></td>
                <GvSharpCell s={c.avg_sharp} />
                <td className="num gv-num">{c.n_entries}</td>
                <td><GvGateBadge gate={c.status} /></td>
                <td><button className="gv-ob-link" type="button">Inspect ›</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const LAYOUTS = [
  { id: 'tree', label: 'Tree', Comp: GvTreeLayout },
  { id: 'master', label: 'Master / detail', Comp: GvMasterDetail },
  { id: 'split', label: 'Concentration', Comp: GvSplitLayout },
];

export default function GameView() {
  // Route param drives the query; falls back to the mock fixture in stub mode.
  const { fixtureId: routeFixtureId } = useParams();
  const { data, isLoading } = useGameView(routeFixtureId);

  const [live, setLive] = useState(false);
  const [tab, setTab] = useState('markets');
  const [layout, setLayout] = useState('tree');

  const { game, mainline, players, combos } = data || {};

  const { max, stats } = useMemo(() => {
    if (!data) return { max: 0, stats: null };
    let max = 0, handle = 0, lo = 0, lu = 0, feeOver = 0, feeUnder = 0;
    let nEntriesOver = 0, nEntriesUnder = 0, comboLegs = 0;
    let slipOverNum = 0, slipOverDen = 0, slipUnderNum = 0, slipUnderDen = 0;
    let topPlayer = { name: '—', dom: 0 };
    for (const p of players) {
      const rp = gvRollPlayer(p);
      handle += rp.fee; nEntriesOver += rp.n_over; nEntriesUnder += rp.n_under; comboLegs += rp.combo;
      if (rp.avg_slip_over != null && rp.n_slips_over) {
        slipOverNum += rp.avg_slip_over * rp.n_slips_over;
        slipOverDen += rp.n_slips_over;
      }
      if (rp.avg_slip_under != null && rp.n_slips_under) {
        slipUnderNum += rp.avg_slip_under * rp.n_slips_under;
        slipUnderDen += rp.n_slips_under;
      }
      if (rp.dom > topPlayer.dom) topPlayer = { name: p.name, dom: rp.dom };
      for (const m of p.markets) for (const ln of m.lines) {
        feeOver += ln.fee_over; feeUnder += ln.fee_under;
        max = Math.max(max, Math.abs(ln.liability_net_over), Math.abs(ln.liability_net_under));
        lo += ln.liability_net_over; lu += ln.liability_net_under;
      }
    }
    const comboLiab = combos.reduce((s, c) => s + c.liability, 0);
    const comboHandle = combos.reduce((s, c) => s + c.fee, 0);
    const worst = Math.max(lo, lu);
    return {
      max,
      stats: {
        handle: handle + comboHandle,
        nEntriesOver,
        nEntriesUnder,
        avgSlipOver: slipOverDen ? slipOverNum / slipOverDen : null,
        avgSlipUnder: slipUnderDen ? slipUnderNum / slipUnderDen : null,
        nCombos: combos.length,
        worst,
        topPlayer,
        feeOver,
        feeUnder,
        comboLiab,
        comboLegs,
      },
    };
  }, [data, players, combos]);

  if (isLoading || !data || !stats) {
    return <div className="rh-gameview"><div className="main-content"><div className="gv-muted" style={{ padding: '2rem' }}>Loading game…</div></div></div>;
  }

  if (!players.length) {
    return (
      <div className="rh-gameview">
        <div className="main-content">
          <div className="gv-muted" style={{ padding: '2rem' }}>
            No quoted markets found for fixture {routeFixtureId || '—'}.
          </div>
        </div>
      </div>
    );
  }

  const ActiveLayout = (LAYOUTS.find((l) => l.id === layout) || LAYOUTS[0]).Comp;

  return (
    <div className="rh-gameview">
      <main className="main-content">
        <div className="gv-breadcrumb">
          <button type="button">Active</button>
          <span className="sep">›</span>
          <button type="button">{game.league}</button>
          <span className="sep">›</span>
          <span>{game.home.name} v {game.away.name}</span>
        </div>

        <GameHead game={game} live={live} />
        <RollupStrip stats={stats} />
        <MainlinePanel mainline={mainline} live={live} />

        <div className="gv-toolbar">
          <div className="gv-tabs">
            <button className={`gv-tab${tab === 'markets' ? ' gv-tab--on' : ''}`} onClick={() => setTab('markets')}>
              Player markets <span className="gv-tab-count">{players.length}</span>
            </button>
            <button className={`gv-tab gv-tab--combos${tab === 'combos' ? ' gv-tab--on' : ''}`} onClick={() => setTab('combos')}>
              Combos <span className="gv-tab-count">{combos.length}</span>
            </button>
          </div>
          <div className="gv-toolbar-right">
            {tab === 'markets' && (
              <div className="gv-seg">
                <span className="gv-seg-label">Layout</span>
                {LAYOUTS.map((l) => (
                  <button key={l.id} className={`gv-seg-btn${layout === l.id ? ' gv-seg-btn--on' : ''}`} onClick={() => setLayout(l.id)}>{l.label}</button>
                ))}
              </div>
            )}
            <div className="gv-seg">
              <button className={`gv-seg-btn${!live ? ' gv-seg-btn--on' : ''}`} onClick={() => setLive(false)}>Pre-game</button>
              <button className={`gv-seg-btn gv-seg-btn--live${live ? ' gv-seg-btn--on' : ''}`} onClick={() => setLive(true)}>Live</button>
            </div>
          </div>
        </div>

        {tab === 'markets'
          ? <ActiveLayout players={players} max={max} live={live} fixtureId={game.fixtureId} />
          : <CombosTab combos={combos} />}
      </main>
    </div>
  );
}
